import React from 'react';

const SECTIONS = [
  {
    num: '1',
    title: 'Introduction',
    content: (
      <>
        <p>
          Welcome to RAKSHA Ireland. We operate a community-powered personal safety network. Your trust and personal
          safety are our highest priorities. This Privacy Policy explains how we collect, use, and protect your
          information when you use our mobile application and promotional website.
        </p>
        <p>
          By registering an account with RAKSHA Ireland, you agree to the collection and use of information in
          accordance with this policy. Because we are an identity-verified community safety network, anonymous
          participation is not possible.
        </p>
      </>
    ),
  },
  {
    num: '2',
    title: 'Information We Collect',
    content: (
      <>
        <p>To provide a secure and reliable safety network, we require users to submit verified credentials during onboarding. We collect:</p>
        <ul className="privacy-bullet-list">
          <li><strong>Personal Identity Information:</strong> Your full legal name, phone number, email address, and profile photo.</li>
          <li><strong>Government-Issued ID:</strong> A scanned copy or photo of a passport, driver licence, or national identity card. This is used solely by the RAKSHA administrator team to verify your identity and is retained in secure encrypted storage for compliance purposes.</li>
          <li><strong>Location Data:</strong> When you are on the Home screen with the app open, your device periodically sends a location update (approximately every 60 seconds or when you move 100 metres). When you trigger an SOS, a one-time precise GPS snapshot is captured and attached to the alert. Background location permission is required so this snapshot can be transmitted immediately even if the app is not in the foreground.</li>
          <li><strong>Device Identifiers:</strong> A combination of device model, operating system, and build ID is used to enforce the single-active-device session policy.</li>
          <li><strong>Push Notification Token:</strong> A device-specific token issued by Firebase Cloud Messaging (FCM) is stored to enable emergency SOS push notifications.</li>
          <li><strong>Alert and History Logs:</strong> Details of SOS alerts you send or respond to, including timestamps, GPS location at time of alert, and resolution status.</li>
        </ul>
      </>
    ),
  },
  {
    num: '3',
    title: 'How We Use Your Data',
    content: (
      <>
        <p>We use the collected information for the following specific purposes:</p>
        <ul className="privacy-bullet-list">
          <li><strong>Identity Verification:</strong> Reviewing and manually approving accounts before allowing access to the safety network.</li>
          <li><strong>Emergency Routing:</strong> Broadcasting your SOS alert including your name, phone number, and GPS location at the moment you tapped SOS to all verified active members of the network so someone nearby can respond.</li>
          <li><strong>60-Minute Alert Window:</strong> An active SOS alert remains visible to all verified community members for 60 minutes from the time it is sent, maximising the chance of multiple responders being aware and able to help.</li>
          <li><strong>Alert History:</strong> After an alert closes (responded to or expired after 60 minutes), the alert record is retained in the history of the person who sent it and the first person who accepted it. All other members do not retain access to that alert.</li>
          <li><strong>Session Management:</strong> Ensuring only one active device session per account to prevent account sharing or hijacking.</li>
          <li><strong>Push Notifications:</strong> Sending emergency SOS alerts and escalation notifications via FCM to registered devices.</li>
          <li><strong>Service Reliability:</strong> Managing system performance and diagnostic metrics.</li>
        </ul>
      </>
    ),
  },
  {
    num: '4',
    title: 'Data Storage and Security',
    content: (
      <>
        <p>
          Your data is stored on encrypted infrastructure hosted within the European Union, specifically
          AWS eu-west-1 (Dublin, Ireland) for the API server and Supabase (EU region) for the database and
          file storage.
        </p>
        <p>
          Government ID documents submitted during registration are stored in a restricted, encrypted Supabase
          Storage bucket and are accessible only to the RAKSHA administration team for identity verification
          purposes. They are retained as part of the verification audit record.
        </p>
        <p>
          All data in transit is protected with SSL/TLS encryption (HTTPS). Authentication uses signed JSON Web
          Tokens (JWT) with a 7-day expiry. Admin passwords are stored using bcrypt hashing. Single-device
          sessions are enforced at the API layer to prevent parallel logins.
        </p>
      </>
    ),
  },
  {
    num: '5',
    title: 'Sharing of Information',
    content: (
      <>
        <p>We <strong>DO NOT</strong> sell, rent, or trade your personal information to third parties or marketing agencies. Your information is only shared under the following conditions:</p>
        <ul className="privacy-bullet-list">
          <li><strong>With the Community Network:</strong> When you trigger an SOS, your name, phone number, and GPS location at the time of the alert are broadcast to all verified active members of the RAKSHA network for up to 60 minutes so that anyone nearby can respond. Only the sender and the first person who accepts the alert retain access to the alert record in their history after it closes.</li>
          <li><strong>With Distress Users:</strong> When you accept an alert to help someone, your name and profile photo are shared with them so they know who is on the way.</li>
          <li><strong>Service Providers:</strong> Firebase (Google) processes push notification delivery. Resend processes transactional email (account approvals, escalation alerts to administrators). These providers are GDPR-compliant and act as data processors under our instructions.</li>
          <li><strong>Legal Compliance:</strong> If required by Irish law or the Garda (police force) to assist in investigating a critical incident.</li>
        </ul>
      </>
    ),
  },
  {
    num: '6',
    title: 'Your Rights and Choices',
    content: (
      <>
        <p>Under GDPR regulations, as a user in Ireland, you have the following rights regarding your data:</p>
        <ul className="privacy-bullet-list">
          <li><strong>Right to Access:</strong> You can request a full copy of the personal data we hold about you.</li>
          <li><strong>Right to Rectification:</strong> You can update your name, phone number, and profile photo at any time via Profile Settings in the app.</li>
          <li><strong>Right to Deletion:</strong> You can request deletion of your account and associated data by contacting us at the address below. Alert history logs that involve other users may be partially retained for safety audit purposes.</li>
          <li><strong>Right to Object:</strong> You can disable background location permission in your device settings at any time, though this will prevent the SOS location snapshot from transmitting if the app is not in the foreground.</li>
        </ul>
      </>
    ),
  },
  {
    num: '7',
    title: 'Terms of Use',
    content: (
      <>
        <p>By using RAKSHA Ireland, you agree to the following terms:</p>
        <ul className="privacy-bullet-list">
          <li><strong>Not a Replacement for Emergency Services:</strong> RAKSHA is a community-powered safety network and is <strong>NOT a replacement for 999 or 112</strong>. In all severe emergencies, always call emergency services first.</li>
          <li><strong>No Guarantee of Response:</strong> RAKSHA makes no guarantees regarding responder arrival times, the availability of nearby members, or the physical actions of any responder.</li>
          <li><strong>Verified Members Only:</strong> You may only register with your own identity. Creating fraudulent accounts, impersonating others, or bypassing identity verification is strictly prohibited.</li>
          <li><strong>Responsible Use:</strong> Triggering false SOS alerts, misusing the system, or harassing community members will result in an immediate permanent ban and may be reported to Irish authorities.</li>
          <li><strong>Single Device Policy:</strong> Each account may only be actively logged in on one device at a time. Logging in on a new device will automatically sign out the previous one.</li>
          <li><strong>Account Approval:</strong> Accounts are subject to administrator review and may be deactivated at any time if misuse is detected.</li>
        </ul>
      </>
    ),
  },
  {
    num: '8',
    title: 'Contact Us',
    content: (
      <>
        <p>If you have any questions, data requests, or feedback regarding this Privacy Policy or Terms of Use, please contact us at:</p>
        <p>
          Email: support@rakshaireland.com
          <br />
          RAKSHA Ireland, Dublin, Ireland
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <main>
      {/* Page Hero */}
      <div className="page-hero">
        <div className="rk-container">
          <span className="page-hero-label">Legal Compliance</span>
          <h1 className="page-hero-title">Privacy Policy &amp; Terms of Use</h1>
          <p className="page-hero-sub">
            How RAKSHA Ireland manages, protects, and handles your safety data securely.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="privacy-body">
        <div className="privacy-meta">
          <i className="bi bi-patch-check-fill"></i>
          Last updated: June 23, 2026
        </div>

        {SECTIONS.map(s => (
          <div className="privacy-section" key={s.num}>
            <h2 className="privacy-section-title">{s.num}. {s.title}</h2>
            {s.content}
          </div>
        ))}
      </div>
    </main>
  );
}
